/**
 * Ô "hiển thị theo dữ liệu mặc định" — mỗi lựa chọn của một danh sách mang sẵn một
 * giá trị mặc định; câu nhận giá trị tự điền giá trị đó khi người nhập chọn, và vẫn
 * sửa lại được. VD chọn tên thuốc BVTV → câu tỷ lệ hoạt chất điền sẵn số của thuốc.
 *
 * Dùng đúng cơ chế XLSForm/ODK nên file xuất ra vẫn chạy được trên ODK:
 *
 *  - Danh sách lựa chọn có thêm cột `default_value` (sheet `choices`).
 *  - Câu nhận giá trị có `calculation` = `instance('<ds>')/root/item[name=${<câu chọn>}]/default_value`
 *    và `trigger` = `${<câu chọn>}`. Có `trigger` thì `calculation` chỉ chạy khi câu
 *    chọn đổi, ô không bị khoá — khác hẳn ô tính toán thường.
 *
 * Renderer, Question Builder và `completeness` phía server (packages/audit-rules) phải
 * hiểu cùng một quy ước; server bỏ qua `calculation` của ô có `trigger` để không tính
 * đè số người nhập đã sửa.
 */

/** Cột phụ của danh sách lựa chọn chứa dữ liệu mặc định. */
export const DEFAULT_VALUE_COLUMN = 'default_value';

const LOOKUP_RE = /^instance\('([\w-]+)'\)\/root\/item\[name=\$\{([\w-]+)\}\]\/(\w+)$/;

/** Tên câu kích hoạt trong cột `trigger` (`${x}` → `x`), hoặc '' nếu không có. */
export function triggerName(row) {
  const m = String(row?.trigger || '').trim().match(/^\$\{\s*([\w-]+)\s*\}$/);
  return m ? m[1] : '';
}

/**
 * Câu hỏi này đang điền sẵn từ câu chọn nào.
 * @returns {{ question: string, list: string, column: string } | null}
 */
export function prefillSourceOf(row) {
  const question = triggerName(row);
  if (!question) return null;
  const m = String(row?.calculation || '').trim().match(LOOKUP_RE);
  if (!m || m[2] !== question) return null;
  return { question, list: m[1], column: m[3] };
}

/** Cặp `calculation` + `trigger` để điền sẵn `column` của lựa chọn đang chọn ở `question`. */
export function prefillColumns(question, list, column = DEFAULT_VALUE_COLUMN) {
  return {
    calculation: `instance('${list}')/root/item[name=\${${question}}]/${column}`,
    trigger: `\${${question}}`,
  };
}

/** Danh sách lựa chọn này có cột dữ liệu mặc định không. */
export function hasDefaultValue(choiceRows) {
  return (choiceRows || []).some((c) => String(c?.[DEFAULT_VALUE_COLUMN] ?? '').trim() !== '');
}

/**
 * Các câu `select_one` có danh sách mang cột dữ liệu mặc định — chính là các câu có
 * thể làm nguồn điền sẵn.
 * @returns {Array<{ name: string, label: string, list: string }>}
 */
export function prefillSources(survey, choices) {
  const byList = new Map();
  (choices || []).forEach((c) => {
    const ln = c?.['list name'];
    if (ln) byList.set(ln, [...(byList.get(ln) || []), c]);
  });
  return (survey || [])
    .map((r) => {
      const [base, list] = String(r?.type || '').trim().split(/\s+/);
      return base === 'select_one' && list && r.name && hasDefaultValue(byList.get(list))
        ? { name: String(r.name), label: String(r.label || r.name), list }
        : null;
    })
    .filter(Boolean);
}
